import { create } from "zustand";
import { persist } from "zustand/middleware";
import { TModelType } from "../shared/types/models";
import { LS_ZAPI_CHAT_STORAGE } from "../shared/local-storage/keys";

export type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  id: string;
};

type TChatKey = `${string}:${string}`;

type ChatHistory = {
  [key: TChatKey]: ChatMessage[];
};

type ChatLoadingState = {
  loadingMessageId: string | null;
  pendingBotMessage: ChatMessage | null;
  pendingGenerationId: number | null;
  isGeneratingChapter: boolean;
  isGeneratingExecutiveSummary: boolean;
  progressMessage: string | null;
  prereqsChaptersToBeGenerated: string[] | null;
  abortController: AbortController | null;
  lastUserMessageRef: { projectId: string; chapterId: string; messageId: string } | null;
  isChapterLoaded?: boolean;
};

export type ChatLoadingStateWithId = ChatLoadingState & {
  chapterId: string;
};

type ChatLoadingStates = {
  [key: TChatKey]: ChatLoadingState;
};

interface ChatState {
  modelName: TModelType;
  pendingChanges: Record<string, boolean>;
  chatHistory: ChatHistory;
  loadingStates: ChatLoadingStates;
  getChatHistory: (projectId: string, chapterId: string) => ChatMessage[];
  addMessage: (projectId: string, chapterId: string, message: ChatMessage) => void;
  clearChatHistory: (projectId: string, chapterId: string) => void;
  removeLastMessage: (projectId: string, chapterId: string) => void;

  getMessagesWithPending: (projectId: string, chapterId: string) => ChatMessage[];

  updatePendingMessage: (projectId: string, chapterId: string, content: string) => void;

  getLoadingState: (projectId: string, chapterId: string) => ChatLoadingState;
  setLoadingState: (projectId: string, chapterId: string, updates: Partial<ChatLoadingState>) => void;
  resetLoadingState: (projectId: string, chapterId: string) => void;
  markChapterAsLoaded: (chapterId: number) => void;

  setModelName: (modelName: TModelType) => void;
}

const createChatKey = (projectId: string, chapterId: string): TChatKey => {
  return `${projectId}:${chapterId}`;
};

const createDefaultLoadingState = (): ChatLoadingState => ({
  loadingMessageId: null,
  pendingBotMessage: null,
  isGeneratingChapter: false,
  isGeneratingExecutiveSummary: false,
  progressMessage: null,
  prereqsChaptersToBeGenerated: null,
  abortController: null,
  lastUserMessageRef: null,
  pendingGenerationId: null
});

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      modelName: "gemini-2.5-pro",
      chatHistory: {},
      loadingStates: {},
      pendingChanges: {},

      getChatHistory: (projectId: string, chapterId: string) => {
        const key = createChatKey(projectId, chapterId);
        const msgs = get().chatHistory[key] || [];
        return msgs;
      },

      addMessage: (projectId: string, chapterId: string, message: ChatMessage) => {
        set((state) => {
          const key = createChatKey(projectId, chapterId);
          const messages = state.chatHistory[key] || [];

          if (messages.some((m) => m.id === message.id)) {
            return state;
          }

          return {
            chatHistory: {
              ...state.chatHistory,
              [key]: [...messages, message]
            }
          };
        });
      },

      clearChatHistory: (projectId: string, chapterId: string) => {
        set((state) => {
          const key = createChatKey(projectId, chapterId);

          return {
            chatHistory: {
              ...state.chatHistory,
              [key]: []
            }
          };
        });
      },

      removeLastMessage: (projectId: string, chapterId: string) => {
        set((state) => {
          const key = createChatKey(projectId, chapterId);
          const messages = state.chatHistory[key] || [];

          if (messages.length === 0) {
            return state;
          }

          return {
            chatHistory: {
              ...state.chatHistory,
              [key]: messages.slice(0, -1)
            }
          };
        });
      },

      getLoadingState: (projectId: string, chapterId: string) => {
        const key = createChatKey(projectId, chapterId);
        return get().loadingStates[key] || createDefaultLoadingState();
      },

      setLoadingState: (projectId: string, chapterId: string, updates: Partial<ChatLoadingState>) => {
        set((state) => {
          const key = createChatKey(projectId, chapterId);
          const currentState = state.loadingStates[key] || createDefaultLoadingState();

          return { loadingStates: { ...state.loadingStates, [key]: { ...currentState, ...updates } } };
        });
      },

      resetLoadingState: (projectId: string, chapterId: string) => {
        set((state) => {
          const key = createChatKey(projectId, chapterId);

          return { loadingStates: { ...state.loadingStates, [key]: createDefaultLoadingState() } };
        });
      },

      markChapterAsLoaded: (chapterId: number) => {
        set((state) => {
          const chapterIdStr = String(chapterId);
          const updatedLoadingStates: ChatLoadingStates = {};

          Object.entries(state.loadingStates).forEach(([key, loadingState]) => {
            const [, keyChapterId] = key.split(":");
            if (keyChapterId === chapterIdStr) {
              updatedLoadingStates[key as TChatKey] = { ...loadingState, isChapterLoaded: true };
            } else {
              updatedLoadingStates[key as TChatKey] = loadingState;
            }
          });

          return { loadingStates: updatedLoadingStates };
        });
      },

      getMessagesWithPending: (projectId: string, chapterId: string) => {
        const messages = get().getChatHistory(projectId, chapterId);
        const loadingState = get().getLoadingState(projectId, chapterId);

        if (loadingState.pendingBotMessage) {
          return [...messages, loadingState.pendingBotMessage];
        }

        return messages;
      },

      updatePendingMessage: (projectId: string, chapterId: string, content: string) => {
        set((state) => {
          const key = createChatKey(projectId, chapterId);
          const currentState = state.loadingStates[key] || createDefaultLoadingState();

          if (!currentState.pendingBotMessage) {
            return state;
          }

          return {
            loadingStates: {
              ...state.loadingStates,
              [key]: {
                ...currentState,
                pendingBotMessage: { ...currentState.pendingBotMessage, content: content }
              }
            }
          };
        });
      },

      setModelName: (modelName: TModelType) => set({ modelName })
    }),
    {
      name: LS_ZAPI_CHAT_STORAGE,
      skipHydration: true,
      partialize: (state) => ({
        modelName: state.modelName,
        chatHistory: state.chatHistory,
        loadingStates: Object.fromEntries(
          Object.entries(state.loadingStates).map(([key, value]) => [
            key,
            {
              ...value,
              abortController: null
            }
          ])
        )
      })
    }
  )
);
